/**
 * BallsService — atomic BALLS transactions with audit trail
 * For real money, this should call a Supabase Edge Function (server-side).
 * For test BALLS, client-side RPC is acceptable.
 */

import { supabase, isSupabaseConfigured } from './supabase';

export interface TransactionResult {
  success: boolean;
  balance?: number;
  error?: string;
}

/**
 * Get current BALLS balance for a user
 */
export async function getBalance(userId: string): Promise<number> {
  if (!isSupabaseConfigured() || !supabase) return 0;
  const { data } = await supabase
    .from('users')
    .select('balls_balance')
    .eq('id', userId)
    .single();
  return data?.balls_balance || 0;
}

/**
 * Deduct BALLS with full transaction logging.
 * Logs balance_before and balance_after for full audit trail.
 */
export async function deductBalls(
  userId: string,
  amount: number,
  type: string,
  description?: string,
  referenceId?: string,
  referenceType?: string
): Promise<TransactionResult> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: 'Database not configured' };
  }

  if (amount <= 0) {
    return { success: false, error: 'Amount must be positive' };
  }

  // Get current balance for audit trail
  const { data: user } = await supabase
    .from('users')
    .select('balls_balance')
    .eq('id', userId)
    .single();

  const currentBalance = user?.balls_balance || 0;
  if (currentBalance < amount) {
    return { success: false, error: 'Insufficient balance', balance: currentBalance };
  }

  const newBalance = currentBalance - amount;

  // Deduct balance
  const { error: updateError } = await supabase
    .from('users')
    .update({ balls_balance: newBalance })
    .eq('id', userId);

  if (updateError) {
    return { success: false, error: updateError.message };
  }

  // Log transaction with full audit trail
  const { error: logError } = await supabase.from('balls_transactions').insert({
    user_id: userId,
    amount: amount,
    transaction_type: 'debit',
    description: description || null,
    balance_before: currentBalance,
    balance_after: newBalance,
    reference_id: referenceId || null,
    reference_type: referenceType || type,
  });

  if (logError) {
    console.error('Failed to log balls_transaction:', logError);
    // Balance was deducted but log failed — still report success
    // The balance change is the source of truth
  }

  return { success: true, balance: newBalance };
}

/**
 * Credit BALLS (test grants, prizes, refunds)
 */
export async function creditBalls(
  userId: string,
  amount: number,
  type: string,
  description?: string,
  referenceId?: string,
  referenceType?: string
): Promise<TransactionResult> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: 'Database not configured' };
  }

  if (amount <= 0) {
    return { success: false, error: 'Amount must be positive' };
  }

  const currentBalance = await getBalance(userId);
  const newBalance = currentBalance + amount;

  const { error } = await supabase
    .from('users')
    .update({ balls_balance: newBalance })
    .eq('id', userId);

  if (error) {
    return { success: false, error: error.message };
  }

  // Log transaction with full audit trail
  const { error: logError } = await supabase.from('balls_transactions').insert({
    user_id: userId,
    amount: amount,
    transaction_type: 'credit',
    description: description || null,
    balance_before: currentBalance,
    balance_after: newBalance,
    reference_id: referenceId || null,
    reference_type: referenceType || type,
  });

  if (logError) {
    console.error('Failed to log balls_transaction:', logError);
  }

  return { success: true, balance: newBalance };
}

/**
 * Get transaction history for a user
 */
export async function getTransactionHistory(userId: string, limit: number = 50) {
  if (!isSupabaseConfigured() || !supabase) return [];

  const { data } = await supabase
    .from('balls_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  return data || [];
}
